import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { parseLocalDate } from '../utils/dates';
import { useFacility } from '../contexts/FacilityContext';
import { TYPE_COLORS } from '../data/facilities';
import { fetchClaimById, fetchPatientById, fetchClaimPayers, fetchClaimRemits, fetchClaimChildren, updateClaimNotes, correctClaim, voidClaim, voidClaimAtPayer, fetchClaimPayerHistory, archiveClaimMdClaims, fetchClaimCreditBalances } from '../services/api';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import COBTimeline from '../components/COBTimeline';
import PayerHistoryPanel from '../components/PayerHistoryPanel';
import AppealModal from '../components/AppealModal';
import ReviewClaimModal from '../components/ReviewClaimModal';
import ClaimMdActivityPanel from '../components/ClaimMdActivityPanel';
import { printClaimFull } from '../utils/printClaim';
import { claimFormLabel } from '../utils/billing';
import './ClaimDetails.css';

const STATUS_CONFIG = {
  pending: { color: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
  submitted: { color: '#3b82f6', bg: '#eff6ff', label: 'Submitted' },
  denied: { color: '#ef4444', bg: '#fef2f2', label: 'Denied' },
  paid: { color: '#05998d', bg: '#f0fdfa', label: 'Paid' },
};

// Workflow (claim_status) labels that don't title-case cleanly on their own.
const CLAIM_STATUS_LABELS = { void_pending: 'Void Pending' };
const formatClaimStatus = (s) =>
  CLAIM_STATUS_LABELS[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

function ClaimDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectFacility, facilities } = useFacility();

  const [claim,        setClaim]        = useState(null);
  const [cobRootClaim, setCobRootClaim] = useState(null);
  const [patient,      setPatient]      = useState(null);
  const [payers,       setPayers]       = useState([]);
  const [remits,       setRemits]       = useState([]);
  const [linkedClaims, setLinkedClaims] = useState([]);
  const [payerHistory, setPayerHistory] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const [claimNotes,    setClaimNotes]    = useState('');
  const [editingNotes,  setEditingNotes]  = useState(false);
  const [notesDraft,    setNotesDraft]    = useState('');
  const [notesSaving,   setNotesSaving]   = useState(false);
  const [notesSaveErr,  setNotesSaveErr]  = useState(null);

  const [appealOpen,    setAppealOpen]    = useState(false);
  const [reviewOpen,    setReviewOpen]    = useState(false);
  const [archiving,     setArchiving]     = useState(false);

  const [creditBalances, setCreditBalances] = useState([]);

  const [voidOpen,      setVoidOpen]      = useState(false);
  const [voidReason,    setVoidReason]    = useState('');
  const [voiding,       setVoiding]       = useState(false);
  const [voidErr,       setVoidErr]       = useState(null);

  useEffect(() => {
    setLoading(true);
    setCobRootClaim(null);
    fetchClaimById(id)
      .then((res) => {
        const c = res.data;
        setClaim(c);
        setClaimNotes(c.notes ?? '');
        if (c.parent_claim_id && c.cob_order > 1) {
          fetchClaimById(c.parent_claim_id)
            .then((pr) => setCobRootClaim(pr.data))
            .catch(() => {});
        }
        if (c.patient_id) {
          return fetchPatientById(c.patient_id).then((pr) => setPatient(pr.data.patient));
        }
      })
      .catch(() => setError('Claim not found'))
      .finally(() => setLoading(false));
  }, [id]);

  // Always load COB timeline data from the primary (root) claim so the full
  // payer chain and child claims are visible regardless of which COB claim is open.
  const cobRootId = claim?.cob_order > 1 && claim?.parent_claim_id
    ? claim.parent_claim_id
    : Number(id);

  const loadCOBData = useCallback(() => {
    fetchClaimPayers(cobRootId).then((res) => setPayers(res.data)).catch(() => setPayers([]));
    fetchClaimRemits(cobRootId).then((res) => setRemits(res.data)).catch(() => setRemits([]));
    fetchClaimChildren(cobRootId).then((res) => setLinkedClaims(res.data)).catch(() => setLinkedClaims([]));
    // Payer-switch history is per-level — keyed on the claim actually being viewed.
    fetchClaimPayerHistory(id).then((res) => setPayerHistory(res.data)).catch(() => setPayerHistory([]));
  }, [cobRootId, id]);

  useEffect(() => { loadCOBData(); }, [loadCOBData]);

  // Credit balances (overpayments owed back to a payer) are recorded against the
  // specific claim that was paid then rebilled — keyed on the claim being viewed.
  const loadCreditBalances = useCallback(() => {
    fetchClaimCreditBalances(id)
      .then((res) => setCreditBalances(res.data || []))
      .catch(() => setCreditBalances([]));
  }, [id]);

  useEffect(() => { loadCreditBalances(); }, [loadCreditBalances]);

  // Resolving happens on the Credit Balances worklist (full modal with amount entry,
  // partial recovery, method picker). Hand off there and auto-open the modal for this
  // credit balance via the ?resolve=<id> query param.
  const handleResolveCredit = (cb) =>
    navigate(`/revenue/credit-balances?resolve=${cb.credit_balance_id}`);

  const facility = claim ? facilities.find((f) => (f.facility_id ?? f.id) === claim.facility_id) : null;

  if (loading) {
    return (
      <div className="cd-page">
        <div className="cd-not-found"><p>Loading…</p></div>
      </div>
    );
  }

  if (error || !claim) {
    return (
      <div className="cd-page">
        <div className="cd-not-found">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <h2>Claim Not Found</h2>
          <p>The claim you are looking for does not exist.</p>
          <Link to="/claims">Back to Claims Ledger</Link>
        </div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[claim.status] || STATUS_CONFIG.pending;
  const canEdit = !['submitted', 'paid'].includes(claim.status);
  const fmt = (n) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handlePrint = () =>
    printClaimFull(claim.claim_id ?? claim.id, facility);

  const handleSaveNotes = async () => {
    setNotesSaving(true);
    setNotesSaveErr(null);
    try {
      const claimId = claim.claim_id ?? claim.id;
      await updateClaimNotes(claimId, notesDraft);
      setClaimNotes(notesDraft);
      setEditingNotes(false);
    } catch {
      setNotesSaveErr('Failed to save notes. Please try again.');
    } finally {
      setNotesSaving(false);
    }
  };

  // Corrected/replacement claim (837 frequency 7) — only for claims the payer has on file.
  const canCorrect =
    ['submitted', 'denied', 'paid'].includes(claim.status) &&
    !['voided', 'void_pending'].includes(claim.claim_status) &&
    !claim.rebilled_to_claim_id;

  const handleCorrect = async () => {
    if (!window.confirm(
      'File a corrected (replacement) claim to the same payer? This creates a new claim that references the original and supersedes it.'
    )) return;
    try {
      let res;
      try {
        res = await correctClaim(claim.id, {});
      } catch (err) {
        if (err?.response?.data?.requires_control_number) {
          const cn = window.prompt('Enter the payer claim control number (ICN) from the original remittance:');
          if (!cn) return;
          res = await correctClaim(claim.id, { payer_control_number: cn.trim() });
        } else {
          throw err;
        }
      }
      // `res` is already the response body — the axios interceptor unwraps `response.data`.
      navigate(`/claims/${res.newClaimId}`);
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Failed to create the corrected claim.');
    }
  };

  // Withdraw at payer — transmit an 837 freq-8 void to cancel a transmitted claim at
  // the insurer (distinct from the internal Void, which only marks it dead in our system).
  const canWithdraw =
    ['submitted', 'denied', 'paid'].includes(claim.status) &&
    !['voided', 'void_pending'].includes(claim.claim_status);

  const handleWithdraw = async () => {
    if (!window.confirm(
      'Send a void to withdraw this claim? This uploads an 837 frequency-8 void to Claim.MD. '
      + 'It is held for transmit approval at the clearinghouse, then cancels the claim at the payer once transmitted.'
    )) return;
    try {
      let res;
      try {
        res = await voidClaimAtPayer({ claim_id: claim.id });
      } catch (err) {
        if (err?.response?.data?.requires_control_number) {
          const cn = window.prompt('Enter the payer claim control number (ICN) from the remittance:');
          if (!cn) return;
          res = await voidClaimAtPayer({ claim_id: claim.id, payer_control_number: cn.trim() });
        } else {
          throw err;
        }
      }
      // `res` is already the response body — the axios interceptor unwraps `response.data`.
      window.alert(res.message || 'Void sent to Claim.MD — pending transmit approval.');
      // Mark void_pending locally (not voided): the void was uploaded but is still
      // awaiting transmit approval at Claim.MD. Set void_submitted_at so the COB
      // timeline shows the "void sent" event without a full refresh; syncResponses
      // flips it to voided once the void is transmitted to the payer.
      setClaim((c) => ({ ...c, claim_status: 'void_pending', void_submitted_at: c.void_submitted_at || new Date().toISOString() }));
      loadCOBData();
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Failed to withdraw the claim at the payer.');
    }
  };

  // Internal void — mark the claim dead in our system (no payer transmission).
  // Self-pay claims drop off the worklist once generated, so the claim detail page
  // is the only place they can be voided. (Distinct from "Withdraw at Payer", which
  // only applies to claims actually submitted to an insurer.)
  const canVoid = !!claim.is_self_pay && claim.claim_status !== 'voided';

  const handleVoidClaim = async () => {
    if (!voidReason.trim()) return;
    setVoiding(true);
    setVoidErr(null);
    try {
      await voidClaim(claim.id, voidReason.trim());
      // Reflect the void locally so the COB timeline shows the internal-void event
      // (claim_status 'voided' + no void_submitted_at) without a full refresh.
      setClaim((c) => ({
        ...c,
        claim_status:       'voided',
        voided_from_status: c.claim_status,
        void_reason:        voidReason.trim(),
        voided_at:          new Date().toISOString(),
      }));
      setVoidOpen(false);
      setVoidReason('');
      loadCOBData();
    } catch (err) {
      setVoidErr(err?.response?.data?.error || 'Failed to void the claim. Please try again.');
    } finally {
      setVoiding(false);
    }
  };

  // Appeal a payer decision — only meaningful once the claim is at the payer
  // (has a Claim.MD id) and has been denied/rejected.
  const canAppeal =
    !!claim.claimmd_id &&
    (claim.status === 'denied' || claim.claim_status === 'rejected');

  // Remove the claim from Claim.MD's "Manage Claims" menu. Available for any
  // claim that was transmitted (has a Claim.MD id). This is housekeeping at the
  // clearinghouse only — it does not change the claim in this system.
  const canArchive = !!claim.claimmd_id;

  // Submit (or resubmit) to Claim.MD straight from the claim page, without hunting for
  // the claim in the worklist. Mirrors the worklist's submit paths: a 'generated' claim
  // is reviewed and transmitted, a 'rejected' claim is corrected and resubmitted. Self-pay
  // claims have no clearinghouse lifecycle, so they're never submitted to the clearinghouse.
  const canSubmitToClaimMd =
    ['generated', 'rejected'].includes(claim.claim_status) &&
    !claim.is_self_pay;

  const handleSubmitted = () => {
    setReviewOpen(false);
    // Re-fetch so the new payment/workflow status (now Pending) is reflected on the page.
    fetchClaimById(id)
      .then((res) => { setClaim(res.data); setClaimNotes(res.data.notes ?? ''); })
      .catch(() => {});
    loadCOBData();
  };

  const handleArchive = async () => {
    if (!window.confirm(
      'Remove this claim from Claim.MD\'s Manage Claims menu?\n\n'
      + 'This only archives it at the clearinghouse — the claim, its status, and '
      + 'remittances in this system are unaffected.'
    )) return;
    setArchiving(true);
    try {
      await archiveClaimMdClaims({ claimIds: [claim.id] });
      window.alert('Claim removed from Claim.MD\'s Manage Claims menu.');
    } catch (err) {
      window.alert(err?.response?.data?.message || err?.response?.data?.error || 'Failed to archive the claim at Claim.MD.');
    } finally {
      setArchiving(false);
    }
  };

  const handleUpdateClaim = () => {
    if (facility) {
      selectFacility(facility);
    }
    navigate('/worklist/generate', {
      state: {
        claim,
        isEdit: true,
        returnTo: `/claims/${claim.id}`,
      },
    });
  };

  return (
    <div className="cd-page">
      <ActiveFacilityBanner />

      {/* Breadcrumb */}
      <div className="cd-breadcrumb">
        <Link to="/claims" className="cd-breadcrumb-link">Claims Ledger</Link>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="cd-breadcrumb-current">{claim.claim_number}</span>
      </div>

      {/* COB child banner — shown when this is a secondary or tertiary claim */}
      {claim.parent_claim_id && claim.cob_order > 1 && (
        <div className="cd-cob-banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <span>
            {claim.cob_order === 2 ? 'Secondary' : 'Tertiary'} COB Claim
          </span>
          <span className="cd-cob-banner-divider" />
          <Link to={`/claims/${claim.parent_claim_id}`} className="cd-cob-banner-link">
            View Primary Claim
          </Link>
        </div>
      )}

      {/* Rebill banners — payer reassignment links */}
      {claim.rebilled_to_claim_id && (
        <div className="cd-cob-banner cd-rebill-banner cd-rebill-banner--superseded">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          <span>
            Superseded — rebilled to a new payer
            {claim.rebill_reason ? ` (${claim.rebill_reason})` : ''}. This claim stays open to receive its original payer&apos;s response.
          </span>
          <span className="cd-cob-banner-divider" />
          <Link to={`/claims/${claim.rebilled_to_claim_id}`} className="cd-cob-banner-link">
            View Rebilled Claim
          </Link>
        </div>
      )}
      {claim.rebilled_from_claim_id && (
        <div className="cd-cob-banner cd-rebill-banner cd-rebill-banner--rebill">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          <span>Rebill — payer reassigned from a prior claim.</span>
          <span className="cd-cob-banner-divider" />
          <Link to={`/claims/${claim.rebilled_from_claim_id}`} className="cd-cob-banner-link">
            View Original Claim
          </Link>
        </div>
      )}

      {/* Void-pending banner — withdrawal sent to Claim.MD, awaiting transmit approval */}
      {claim.claim_status === 'void_pending' && (
        <div className="cd-cob-banner cd-rebill-banner cd-rebill-banner--superseded">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>
            Void pending — the withdrawal was sent to Claim.MD and is awaiting transmit approval.
            The claim will show as voided once the void is transmitted to the payer.
          </span>
        </div>
      )}

      {/* Credit balance banners — overpayments owed back to a payer (refund/recoup) */}
      {creditBalances
        .filter((cb) => Number(cb.open_balance) > 0)
        .map((cb) => {
          const overdue = cb.aging_days > 60;
          return (
            <div key={cb.credit_balance_id} className={`cd-cob-banner cd-credit-banner${overdue ? ' cd-credit-banner--overdue' : ''}`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <span>
                Credit balance — <span className="cd-credit-banner-amount">{fmt(Number(cb.open_balance))}</span> owed back
                to {cb.payer_name || 'the original payer'}
                {cb.compliance_due_date && (
                  <> · <span className="cd-credit-banner-due">due {parseLocalDate(cb.compliance_due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span></>
                )}
                {overdue ? ' (past 60-day deadline)' : ''}
              </span>
              <span className="cd-credit-banner-spacer" />
              <button className="cd-credit-banner-btn" onClick={() => handleResolveCredit(cb)}>
                Resolve
              </button>
            </div>
          );
        })}

      {/* Header */}
      <div className="cd-header">
        <div className="cd-header-left">
          <div className="cd-title-row">
            <h1 className="cd-title">{claim.claim_number}</h1>
            <span className="cd-status-badge" style={{ color: sc.color, backgroundColor: sc.bg }}>
              <span className="cd-status-dot" style={{ background: sc.color }} />
              {sc.label}
            </span>
            {claim.claim_status && (
              <span className={`cd-workflow-badge cd-workflow-${claim.claim_status}`}>
                {formatClaimStatus(claim.claim_status)}
              </span>
            )}
          </div>
          <p className="cd-subtitle">
            Encounter {claim.encounter_id} &middot; {facility?.name || 'Unknown Facility'}
          </p>
        </div>
        <div className="cd-header-actions">
          <button className="cd-btn-secondary" onClick={handlePrint}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </button>
          {canSubmitToClaimMd && (
            <button className="cd-btn-submit" onClick={() => setReviewOpen(true)} title="Review and submit this claim to Claim.MD">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              {claim.claim_status === 'rejected' ? 'Resubmit to Claim.MD' : 'Submit to Claim.MD'}
            </button>
          )}
          {canCorrect && (
            <button className="cd-btn-secondary" onClick={handleCorrect} title="File a corrected/replacement claim (837 frequency 7) to the same payer">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Correct &amp; Resend
            </button>
          )}
          {canWithdraw && (
            <button className="cd-btn-withdraw" onClick={handleWithdraw} title="Transmit an 837 void to cancel this claim at the payer">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              Withdraw at Payer
            </button>
          )}
          {canVoid && (
            <button className="cd-btn-void" onClick={() => { setVoidReason(''); setVoidErr(null); setVoidOpen(true); }} title="Void this self-pay claim — marks it dead in this system">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
              </svg>
              Void Claim
            </button>
          )}
          {canAppeal && (
            <button className="cd-btn-secondary" onClick={() => setAppealOpen(true)} title="Start a payer appeal through Claim.MD">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
              Appeal
            </button>
          )}
          {canArchive && (
            <button className="cd-btn-secondary" onClick={handleArchive} disabled={archiving} title="Remove this claim from Claim.MD's Manage Claims menu">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
              </svg>
              {archiving ? 'Archiving…' : 'Archive'}
            </button>
          )}
          {canEdit ? (
            <button className="cd-btn-update" onClick={handleUpdateClaim}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
              </svg>
              Update Claim
            </button>
          ) : (
            <div className="cd-claim-locked">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Locked · {sc.label}
            </div>
          )}
        </div>
      </div>

      {/* Main info grid */}
      <div className="cd-info-grid">
        {/* Claim Information */}
        <div className="cd-card cd-full-card">
          <div className="cd-card-header">
            <h3 className="cd-card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Claim Information
            </h3>
          </div>
          <div className="cd-card-body">
            <div className="cd-detail-grid">
              <div className="cd-detail-item">
                <span className="cd-detail-label">Claim Number</span>
                <span className="cd-detail-value">{claim.claim_number}</span>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">Encounter ID</span>
                <span className="cd-detail-value">{claim.encounter_id}</span>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">Service Date</span>
                <span className="cd-detail-value">
                  {parseLocalDate(claim.service_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">Provider</span>
                <span className="cd-detail-value">{claim.provider}</span>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">Amount</span>
                <span className="cd-detail-value cd-amount">{fmt(claim.amount)}</span>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">Claim Form</span>
                <span className="cd-detail-value">{claimFormLabel(claim.form_type, claim.billing_component)}</span>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">CPT / HCPCS Codes</span>
                <div className="cd-cpt-list">
                  {claim.cpt_codes.map((c) => (
                    <span key={c} className="cd-cpt-tag">{c}</span>
                  ))}
                </div>
              </div>
              <div className="cd-detail-item">
                <span className="cd-detail-label">Payment Status</span>
                <span className="cd-status-badge" style={{ color: sc.color, backgroundColor: sc.bg, fontSize: '0.78rem' }}>
                  <span className="cd-status-dot" style={{ background: sc.color }} />
                  {sc.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Patient Card */}
        <div className="cd-card">
          <div className="cd-card-header">
            <h3 className="cd-card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
              </svg>
              Patient
            </h3>
          </div>
          {patient ? (
            <Link to={`/patients/${patient.id}`} className="cd-entity-card">
              <div className="cd-entity-avatar patient">
                {patient.first_name?.[0]}{patient.last_name?.[0]}
              </div>
              <div className="cd-entity-info">
                <p className="cd-entity-name">{patient.first_name} {patient.last_name}</p>
                <div className="cd-entity-meta">
                  <span className="cd-entity-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {parseLocalDate(patient.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="cd-entity-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.11 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                    {patient.phone}
                  </span>
                  <span className="cd-entity-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    {patient.insurance_provider}
                  </span>
                </div>
              </div>
              <svg className="cd-entity-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Link>
          ) : (
            <div className="cd-card-body">
              <div className="cd-detail-item">
                <span className="cd-detail-label">Patient Name</span>
                <span className="cd-detail-value">{claim.patient_name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Facility Card */}
        <div className="cd-card">
          <div className="cd-card-header">
            <h3 className="cd-card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Facility
            </h3>
          </div>
          {facility ? (
            <Link to={`/facility/${facility.id}`} className="cd-entity-card">
              <div className="cd-entity-avatar facility">
                {facility.name.split(' - ').pop().substring(0, 2).toUpperCase()}
              </div>
              <div className="cd-entity-info">
                <p className="cd-entity-name">{facility.name}</p>
                <div className="cd-entity-meta">
                  <span className="cd-entity-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    {facility.city}, {facility.state}
                  </span>
                  <span className="cd-entity-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                    NPI: {facility.npi}
                  </span>
                </div>
              </div>
              <span className="cd-entity-badge" style={{ background: TYPE_COLORS[facility.type] || '#171363' }}>
                {facility.type}
              </span>
              <svg className="cd-entity-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Link>
          ) : (
            <div className="cd-card-body">
              <div className="cd-detail-item">
                <span className="cd-detail-label">Facility</span>
                <span className="cd-detail-value">{getFacilityName(claim.facility_id)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Payer / Submission History — only renders when there's a rebill chain */}
        <PayerHistoryPanel history={payerHistory} currentClaimId={Number(id)} />

        {/* Coordination of Benefits Timeline */}
        <div className="cd-card cd-full-card">
          <div className="cd-card-header">
            <h3 className="cd-card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              Claim Submission Timeline- Coordination of Benefits
            </h3>
          </div>
          <div className="cd-card-body">
            <COBTimeline
              claim={cobRootClaim || claim}
              payers={payers}
              remits={remits}
              linkedClaims={linkedClaims}
              onRemitSaved={loadCOBData}
            />
          </div>
        </div>

        {/* Claim Notes */}
        <div className="cd-card cd-full-card">
          <div className="cd-card-header">
            <h3 className="cd-card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Claim Notes
            </h3>
            {!editingNotes && (
              <button
                className="cd-notes-edit-btn"
                onClick={() => { setNotesDraft(claimNotes); setEditingNotes(true); setNotesSaveErr(null); }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
                Edit
              </button>
            )}
          </div>
          <div className="cd-card-body">
            {editingNotes ? (
              <div className="cd-notes-editor">
                <textarea
                  className="cd-notes-textarea"
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Add notes about this claim..."
                  rows={5}
                  autoFocus
                />
                {notesSaveErr && <p className="cd-notes-error">{notesSaveErr}</p>}
                <div className="cd-notes-actions">
                  <button
                    className="cd-btn-secondary"
                    onClick={() => { setEditingNotes(false); setNotesSaveErr(null); }}
                  >
                    Cancel
                  </button>
                  <button className="cd-btn-update" onClick={handleSaveNotes} disabled={notesSaving}>
                    {notesSaving ? 'Saving…' : 'Save Notes'}
                  </button>
                </div>
              </div>
            ) : claimNotes ? (
              <p className="cd-notes-text">{claimNotes}</p>
            ) : (
              <p className="cd-notes-empty">No notes recorded for this claim.</p>
            )}
          </div>
        </div>

        {claim.claimmd_id && <ClaimMdActivityPanel claim={claim} />}
      </div>

      {appealOpen && (
        <AppealModal claim={claim} onClose={() => setAppealOpen(false)} />
      )}

      {reviewOpen && (
        <ReviewClaimModal
          claim={claim}
          facility={facility}
          onClose={() => setReviewOpen(false)}
          onSubmitted={handleSubmitted}
        />
      )}

      {voidOpen && (
        <div className="cd-void-backdrop" onClick={() => !voiding && setVoidOpen(false)}>
          <div className="cd-void-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cd-void-modal-header">
              <div className="cd-void-modal-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
              </div>
              <div>
                <h2 className="cd-void-modal-title">Void Claim</h2>
                <p className="cd-void-modal-subtitle">This action cannot be undone. The claim will be permanently marked voided.</p>
              </div>
            </div>

            <div className="cd-void-modal-meta">
              <div className="cd-void-modal-meta-item">
                <span className="cd-void-modal-meta-label">Claim</span>
                <span className="cd-void-modal-meta-value">{claim.claim_number || '—'}</span>
              </div>
              <div className="cd-void-modal-meta-item">
                <span className="cd-void-modal-meta-label">Patient</span>
                <span className="cd-void-modal-meta-value">{claim.patient_name}</span>
              </div>
              <div className="cd-void-modal-meta-item">
                <span className="cd-void-modal-meta-label">Current Status</span>
                <span className="cd-void-modal-meta-value">{claim.claim_status || '—'}</span>
              </div>
            </div>

            <label className="cd-void-modal-label" htmlFor="cd-void-reason">
              Reason for voiding <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              id="cd-void-reason"
              className="cd-void-modal-textarea"
              placeholder="Describe why this claim is being voided..."
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={3}
              disabled={voiding}
              autoFocus
            />
            {voidErr && <p className="cd-notes-error">{voidErr}</p>}

            <div className="cd-void-modal-actions">
              <button className="cd-btn-secondary" onClick={() => { setVoidOpen(false); setVoidReason(''); }} disabled={voiding}>
                Cancel
              </button>
              <button className="cd-btn-void-confirm" onClick={handleVoidClaim} disabled={voiding || !voidReason.trim()}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                </svg>
                {voiding ? 'Voiding…' : 'Void Claim'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClaimDetails;
